import { useCallback, useState, useEffect, useRef } from "react";
import { useAuth } from "@clerk/react";
import { track } from "@/lib/analytics";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ReactFlow,
  Background,
  BackgroundVariant,
  type NodeTypes,
  type EdgeTypes,
  type ReactFlowInstance,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";

import { usePipelineStore } from "@/lib/stores/pipeline-store";
import type { PipelineNode, PipelineEdge } from "@/lib/types/pipeline";
import { notify } from "@/components/ui/notification";
import { getModuleDefinition } from "@/lib/registry/modules";
import type { DataType } from "@/lib/types/module";
import { PARAM_TYPE_TO_DATA_TYPE } from "@/lib/types/model-catalog";
import { getStructuredOutputHandles, type OutputSchemaField } from "@/lib/registry/text-builder";
import { useModelCatalogStore } from "@/lib/stores/model-catalog-store";
import { ModuleNode } from "./nodes/ModuleNode";
import { AgentNode } from "./nodes/AgentNode";
import { TypedEdge } from "./edges/TypedEdge";
import { ModulePalette, type PendingConnection } from "./ModulePalette";
import { CanvasToolbar } from "./CanvasToolbar";
import { CanvasContextMenu } from "./CanvasContextMenu";
import { ApprovalBanner } from "./ApprovalBanner";
import { createProjectsApi, type Project } from "@/lib/api/projects";
import { createRunsApi } from "@/lib/api/runs";
import { useExecutionStore } from "@/lib/stores/execution-store";
import { useTimingStatsStore } from "@/lib/stores/timing-stats-store";

const nodeTypes: NodeTypes = { module: ModuleNode, agent_node: AgentNode };
const edgeTypes: EdgeTypes = { typed: TypedEdge };

const defaultEdgeOptions = { type: "typed" };

interface CanvasPanelProps {
  project: Project;
}

export function CanvasPanel({ project }: CanvasPanelProps) {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  const nodes = usePipelineStore((s) => s.nodes);
  const edges = usePipelineStore((s) => s.edges);
  const isDirty = usePipelineStore((s) => s.isDirty);
  const onNodesChange = usePipelineStore((s) => s.onNodesChange);
  const onEdgesChange = usePipelineStore((s) => s.onEdgesChange);
  const onConnect = usePipelineStore((s) => s.onConnect);
  const isValidConnection = usePipelineStore((s) => s.isValidConnection);
  const getRejectionReason = usePipelineStore((s) => s.getRejectionReason);
  const selectNode = usePipelineStore((s) => s.selectNode);
  const loadPipeline = usePipelineStore((s) => s.loadPipeline);
  const needsFitView = usePipelineStore((s) => s.needsFitView);
  const clearFitView = usePipelineStore((s) => s.clearFitView);
  const focusedNodeId = usePipelineStore((s) => s.focusedNodeId);

  // Execution state
  const isRunning = useExecutionStore((s) => s.isRunning);
  const runId = useExecutionStore((s) => s.runId);
  const runStatus = useExecutionStore((s) => s.runStatus);
  const awaitingGateNodeId = useExecutionStore((s) => s.awaitingGateNodeId);
  const startRun = useExecutionStore((s) => s.startRun);
  const resumeRun = useExecutionStore((s) => s.resumeRun);
  const cancelRun = useExecutionStore((s) => s.cancelRun);
  const setOnRunNode = useExecutionStore((s) => s.setOnRunNode);
  const setOnApprove = useExecutionStore((s) => s.setOnApprove);
  const setOnReject = useExecutionStore((s) => s.setOnReject);
  const syncRunStatus = useExecutionStore((s) => s.syncRunStatus);
  const runError = useExecutionStore((s) => s.runError);
  const dismissRunError = useExecutionStore((s) => s.dismissRunError);
  useEffect(() => {
    if (!runError) return;
    notify.error("Pipeline failed", { description: runError, duration: 6000 });
    dismissRunError();
  }, [runError, dismissRunError]);
  const _ws = useExecutionStore((s) => s._ws);
  const fetchTimingStats = useTimingStatsStore((s) => s.fetch);
  const runsApi = createRunsApi({ getToken });
  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);

  const handleRunPipeline = useCallback(async () => {
    const run = await runsApi.start(project.id, { mode: "full" });
    track("pipeline_run_started", { project_id: project.id, mode: "full" });
    startRun(run);
  }, [project.id, runsApi, startRun]);

  const handleCancelRun = useCallback(async () => {
    if (runStatus === "awaiting_approval" && runId) {
      // Cancel during approval = reject: the orchestrator already exited,
      // so the Redis cancel key would go unread. Use the approval endpoint instead.
      setIsRejecting(true);
      try {
        await runsApi.approve(project.id, runId, { action: "reject" });
      } finally {
        cancelRun();
        setIsRejecting(false);
      }
      return;
    }
    try {
      if (runId) await runsApi.cancel(project.id, runId);
    } catch { /* backend may have already terminated — still clear frontend state */ }
    cancelRun();
  }, [project.id, runId, runStatus, runsApi, cancelRun]);

  const handleRunNode = useCallback(async (nodeId: string) => {
    const run = await runsApi.start(project.id, { mode: "node", targetNodeId: nodeId });
    startRun(run);
  }, [project.id, runsApi, startRun]);

  // Register per-node run callback so ModuleNode can trigger it
  useEffect(() => {
    setOnRunNode(handleRunNode);
  }, [setOnRunNode, handleRunNode]);

  const handleRunFromHere = useCallback(async (nodeId: string) => {
    const run = await runsApi.start(project.id, { mode: "from_here", targetNodeId: nodeId });
    startRun(run);
  }, [project.id, runsApi, startRun]);

  const handleApprove = useCallback(async () => {
    if (!runId) return;
    setIsApproving(true);
    try {
      const newRun = await runsApi.approve(project.id, runId, { action: "approve" });
      if (newRun) resumeRun(newRun);
    } finally {
      setIsApproving(false);
    }
  }, [project.id, runId, runsApi, resumeRun]);

  const handleReject = useCallback(async () => {
    if (!runId) return;
    setIsRejecting(true);
    try {
      await runsApi.approve(project.id, runId, { action: "reject" });
    } finally {
      // Always cancel client-side regardless of API success — if the API threw
      // (e.g. null body parsing), the backend still processed the rejection.
      cancelRun();
      setIsRejecting(false);
    }
  }, [project.id, runId, runsApi, cancelRun]);

  // Register approval callbacks so ApprovalGateContent and ApprovalBanner can trigger them
  useEffect(() => {
    setOnApprove(handleApprove);
  }, [setOnApprove, handleApprove]);

  useEffect(() => {
    setOnReject(handleReject);
  }, [setOnReject, handleReject]);

  // Refresh timing stats after every run so the next run's % uses up-to-date p50 values.
  const prevIsRunningRef = useRef(false);
  useEffect(() => {
    if (prevIsRunningRef.current && !isRunning) {
      fetchTimingStats(getToken);
    }
    prevIsRunningRef.current = isRunning;
  }, [isRunning, getToken, fetchTimingStats]);

  // Fallback: if WebSocket drops while a run is active, poll the REST API until terminal.
  // This handles proxy idle-timeouts (common with long-running nodes like video/image gen).
  useEffect(() => {
    if (!isRunning || !runId || _ws !== null) return;
    let stopped = false;
    let delay = 3000;
    const poll = async () => {
      while (!stopped) {
        await new Promise<void>((r) => setTimeout(r, delay));
        if (stopped) break;
        delay = Math.min(delay * 1.5, 15000);
        try {
          const run = await runsApi.get(project.id, runId);
          if (["completed", "failed", "cancelled"].includes(run.status)) {
            syncRunStatus(run.status);
            break;
          }
        } catch {
          // network error — keep retrying
        }
      }
    };
    poll();
    return () => { stopped = true; };
  }, [isRunning, runId, _ws, project.id, syncRunStatus]);

  // Safety net: poll run status every 30 s even when WS appears connected.
  // Guards against Redis pub/sub message loss (WS stays open but events are silently dropped).
  useEffect(() => {
    if (!isRunning || !runId) return;
    const id = setInterval(async () => {
      try {
        const run = await runsApi.get(project.id, runId);
        if (["completed", "failed", "cancelled"].includes(run.status)) {
          syncRunStatus(run.status);
        }
      } catch { /* non-fatal */ }
    }, 30_000);
    return () => clearInterval(id);
  }, [isRunning, runId, project.id, syncRunStatus]);

const [paletteOpen, setPaletteOpen] = useState(false);
  const [insertPosition, setInsertPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [pendingConnection, setPendingConnection] =
    useState<PendingConnection | null>(null);
  const [rfInstance, setRfInstance] = useState<ReactFlowInstance<
    PipelineNode,
    PipelineEdge
  > | null>(null);

  const handleViewGateOnCanvas = useCallback(() => {
    if (!awaitingGateNodeId || !rfInstance) return;
    rfInstance.fitView({
      nodes: [{ id: awaitingGateNodeId }],
      duration: 600,
      padding: 0.8,
      maxZoom: 1.5,
    });
    selectNode(awaitingGateNodeId);
  }, [awaitingGateNodeId, rfInstance, selectNode]);

  // Context menu state
  const [contextMenuPos, setContextMenuPos] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [contextMenuNodeId, setContextMenuNodeId] = useState<string | null>(
    null,
  );

  // Load project graph into pipeline store.
  // Re-runs when the server-side graph changes (e.g. after agent mutations refetch the project),
  // but skips the reload when the user has unsaved local changes (isDirty) to avoid resetting work.
  const lastLoadedGraphRef = useRef<string | null>(null);
  useEffect(() => {
    if (!project?.graph) return;
    const graphStr = JSON.stringify(project.graph);
    // First mount: always load. Subsequent server updates: only load when canvas is clean.
    if (lastLoadedGraphRef.current !== null && isDirty) return;
    if (lastLoadedGraphRef.current === graphStr) return;
    lastLoadedGraphRef.current = graphStr;
    loadPipeline(
      (project.graph.nodes as PipelineNode[]) || [],
      (project.graph.edges as PipelineEdge[]) || [],
    );
  }, [project, loadPipeline, isDirty]);

  // Fit view after build_pipeline loads new nodes
  useEffect(() => {
    if (needsFitView && rfInstance) {
      // Small delay to let React Flow measure the new nodes
      const timer = setTimeout(() => {
        rfInstance.fitView({ duration: 400, padding: 0.15 });
        clearFitView();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [needsFitView, rfInstance, clearFitView]);

  // Auto-save: debounced mutation to save graph changes
  const projectsApi = createProjectsApi({ getToken });
  const saveGraphMutation = useMutation({
    mutationFn: (graph: { nodes: unknown[]; edges: unknown[] }) =>
      projectsApi.update(project.id, { graph }),
    onSuccess: () => {
      // Invalidate project query to update last edited time
      queryClient.invalidateQueries({ queryKey: ["project", project.id] });
      queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
    onError: () => {
      notify.error("Changes not saved", {
        description: "Your pipeline could not be saved. Check your connection.",
      });
    },
  });

  // Keep a stable ref to the latest mutate fn so callbacks don't go stale
  const saveGraphMutateRef = useRef(saveGraphMutation.mutate);
  useEffect(() => { saveGraphMutateRef.current = saveGraphMutation.mutate; });

  // Immediate save helper — cancels any pending debounce and saves right now
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saveImmediately = useCallback(() => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
    const state = usePipelineStore.getState();
    saveGraphMutateRef.current({ nodes: state.nodes, edges: state.edges });
  }, []);

  // Debounced auto-save for position/config changes
  useEffect(() => {
    if (!isDirty) return;

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(() => {
      saveGraphMutation.mutate({ nodes, edges });
    }, 1000);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [isDirty, nodes, edges]);

  // Warn before refresh/close if there are unsaved changes
  const isDirtyRef = useRef(isDirty);
  useEffect(() => { isDirtyRef.current = isDirty; }, [isDirty]);
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirtyRef.current) e.preventDefault();
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  useEffect(() => {
    if (!focusedNodeId || !rfInstance) return;
    rfInstance.fitView({
      nodes: [{ id: focusedNodeId }],
      duration: 600,
      padding: 0.5,
      maxZoom: 1.5,
    });
    selectNode(focusedNodeId);
  }, [focusedNodeId, rfInstance, selectNode]);

  // Track whether the most recent connection drag was accepted (used to detect rejections)
  const connectionAccepted = useRef(false);

  // Wrap onConnect to save immediately — edges are intentional, don't debounce
  const handleConnect = useCallback(
    (connection: Parameters<typeof onConnect>[0]) => {
      connectionAccepted.current = true;
      onConnect(connection);
      saveImmediately();
    },
    [onConnect, saveImmediately],
  );

  // Cmd+K to open palette
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "k") {
      e.preventDefault();
      setInsertPosition(null);
      setPendingConnection(null);
      setPaletteOpen(true);
    }
  }, []);

  // Double-click canvas to open palette at that position
  const handleDoubleClick = useCallback(
    (e: React.MouseEvent) => {
      if (!rfInstance) return;
      const position = rfInstance.screenToFlowPosition({
        x: e.clientX,
        y: e.clientY,
      });
      setInsertPosition(position);
      setPendingConnection(null);
      setPaletteOpen(true);
    },
    [rfInstance],
  );

  const handleAddNode = useCallback(() => {
    setInsertPosition(null);
    setPendingConnection(null);
    setPaletteOpen(true);
  }, []);

  // Open palette at a specific screen position (from context menu)
  const handleAddNodeAtPosition = useCallback(
    (screenPos: { x: number; y: number }) => {
      if (!rfInstance) return;
      const flowPos = rfInstance.screenToFlowPosition(screenPos);
      setInsertPosition(flowPos);
      setPendingConnection(null);
      setPaletteOpen(true);
    },
    [rfInstance],
  );

  // When a connection drag ends on empty canvas, open palette filtered by type
  const handleConnectEnd = useCallback(
    (
      _event: MouseEvent | TouchEvent,
      connectionState: {
        fromHandle: { id?: string | null; nodeId: string; type: string } | null;
        fromNode: { id: string } | null;
        toNode: unknown;
        to: { x: number; y: number } | null;
      },
    ) => {
      if (!connectionState.fromHandle || !connectionState.fromNode) return;

      // Dropped on a node but onConnect wasn't called → connection was rejected
      if (connectionState.toNode && !connectionAccepted.current) {
        notify.error("Can't connect: incompatible types", {
          description: "Handles must share the same data type (e.g. text → text).",
          duration: 4000,
        })
        return
      }
      connectionAccepted.current = false

      const fromNodeId = connectionState.fromNode.id;
      const fromHandleId = connectionState.fromHandle.id;
      const fromHandleType = connectionState.fromHandle.type as
        | "source"
        | "target";

      if (!fromHandleId) return;

      // Look up the data type of the handle we dragged from
      const fromNode = nodes.find((n) => n.id === fromNodeId);
      if (!fromNode) return;

      const moduleDef = getModuleDefinition(fromNode.data.module);
      if (!moduleDef) return;

      const getModelEntry = useModelCatalogStore.getState().getById;
      const modelEntry = moduleDef.modelConfig
        ? getModelEntry(String(fromNode.data.config.model ?? ""))
        : undefined;

      let dataType: DataType | undefined;
      if (fromHandleType === "source") {
        // For llm_text with structured output schema, handles are dynamic
        if (
          fromNode.data.module === "llm_text" &&
          Array.isArray(fromNode.data.config.output_schema) &&
          (fromNode.data.config.output_schema as OutputSchemaField[]).length > 0
        ) {
          const schema = fromNode.data.config.output_schema as OutputSchemaField[];
          dataType = getStructuredOutputHandles(schema).find((o) => o.name === fromHandleId)?.type;
        }
        if (!dataType) {
          dataType = moduleDef.outputs.find((o) => o.name === fromHandleId)?.type;
        }
      } else {
        // Check static registry inputs first
        const registryInput = moduleDef.inputs.find(
          (i) => i.name === fromHandleId && i.handle,
        );
        dataType = registryInput?.type;

        // Fall back to model parameter handles (Temperature, Max Tokens, etc.)
        if (!dataType && modelEntry) {
          const param = modelEntry.parameters_schema?.params?.find(
            (p) => p.name === fromHandleId,
          );
          if (param) {
            dataType = PARAM_TYPE_TO_DATA_TYPE[param.type];
          }
        }
      }

      if (!dataType) return;

      // Convert screen coords to flow coords (connectionState.to is in screen space)
      const screenPosition = connectionState.to;
      if (!screenPosition || !rfInstance) return;

      const dropPosition = rfInstance.screenToFlowPosition(screenPosition);
      setInsertPosition(dropPosition);
      setPendingConnection({
        nodeId: fromNodeId,
        handleId: fromHandleId,
        handleType: fromHandleType,
        dataType,
      });
      setPaletteOpen(true);
    },
    [nodes, rfInstance, getRejectionReason],
  );

  // Clear pending connection when palette closes
  const handlePaletteOpenChange = useCallback((open: boolean) => {
    setPaletteOpen(open);
    if (!open) setPendingConnection(null);
  }, []);

  // Context menu handlers
  const handlePaneContextMenu = useCallback(
    (e: MouseEvent | React.MouseEvent) => {
      e.preventDefault();
      setContextMenuNodeId(null);
      setContextMenuPos({ x: e.clientX, y: e.clientY });
    },
    [],
  );

  const handleNodeContextMenu = useCallback(
    (e: React.MouseEvent, node: PipelineNode) => {
      e.preventDefault();
      setContextMenuNodeId(node.id);
      setContextMenuPos({ x: e.clientX, y: e.clientY });
    },
    [],
  );

  const closeContextMenu = useCallback(() => {
    setContextMenuPos(null);
    setContextMenuNodeId(null);
  }, []);

  const handleFitView = useCallback(() => {
    rfInstance?.fitView();
  }, [rfInstance]);

  // Close context menu on pane click
  const handlePaneClick = useCallback(() => {
    selectNode(null);
    closeContextMenu();
  }, [selectNode, closeContextMenu]);

  return (
    <div className="flex h-full w-full flex-col gap-2 pb-2" onKeyDown={handleKeyDown} tabIndex={-1}>
      {runStatus === "awaiting_approval" && (
        <ApprovalBanner
          onApprove={handleApprove}
          onReject={handleReject}
          onViewOnCanvas={handleViewGateOnCanvas}
          isApproving={isApproving}
          isRejecting={isRejecting}
        />
      )}
      <div className="flex-1 overflow-hidden rounded-xl border border-border/50 dark:border-border bg-muted dark:bg-zinc-1000">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          defaultEdgeOptions={defaultEdgeOptions}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={handleConnect}
          onConnectStart={() => { connectionAccepted.current = false; }}
          isValidConnection={isValidConnection}
          onNodeClick={(_, node) => {
            selectNode(node.id);
            closeContextMenu();
          }}
          onPaneClick={handlePaneClick}
          onDoubleClick={handleDoubleClick}
          onConnectEnd={handleConnectEnd}
          onInit={setRfInstance}
          onPaneContextMenu={handlePaneContextMenu}
          onNodeContextMenu={handleNodeContextMenu}
          fitView
          proOptions={{ hideAttribution: true }}
          connectionLineStyle={{ stroke: "var(--brand)", strokeWidth: 2 }}
          snapToGrid
          snapGrid={[16, 16]}
          deleteKeyCode={["Backspace", "Delete"]}
        >
          <Background variant={BackgroundVariant.Dots} gap={16} size={1} />
          <CanvasToolbar
            onAddNode={handleAddNode}
            onRunPipeline={handleRunPipeline}
            onCancelRun={handleCancelRun}
            isRunning={isRunning}
          />
          <ModulePalette
            open={paletteOpen}
            onOpenChange={handlePaletteOpenChange}
            insertPosition={insertPosition}
            pendingConnection={pendingConnection}
          />
        </ReactFlow>
        <CanvasContextMenu
          position={contextMenuPos}
          nodeId={contextMenuNodeId}
          onClose={closeContextMenu}
          onAddNode={handleAddNodeAtPosition}
          onFitView={handleFitView}
          onRunNode={handleRunNode}
          onRunFromHere={handleRunFromHere}
        />
      </div>
    </div>
  );
}
